
/**
 * Checks if an article ID is from mock data (numeric) or real database (UUID)
 */
export const isMockArticleId = (id: string) => /^\d+$/.test(id);

/**
 * Formats article ID for database operations, ensuring compatibility with both
 * mock articles (numeric IDs) and real database articles (UUIDs)
 */
export const formatArticleId = (id: string) => {
  // For mock articles with numeric IDs, we need to convert to a proper UUID format
  // that the database will accept as a valid UUID
  if (isMockArticleId(id)) {
    // Generate a deterministic UUID v4 format based on the numeric ID
    // This ensures the same numeric ID always maps to the same UUID
    const paddedId = id.padStart(3, '0');
    return `00000000-0000-4000-a000-000000000${paddedId}`;
  }
  // If it's already a UUID, return it unchanged
  return id;
};

/**
 * Converts formatted article ID back to its original form for frontend use
 */
export const unformatArticleId = (formattedId: string) => {
  // Check if this is one of our specially formatted mock IDs
  if (formattedId && formattedId.startsWith('00000000-0000-4000-a000-')) {
    // Extract the numeric portion at the end and remove leading zeros
    return formattedId.substring(formattedId.length - 3).replace(/^0+/, '');
  }
  return formattedId;
};

/**
 * Ensures article exists in the database before allowing interactions
 * @param articleId The ID of the article to validate
 * @param articleExists Optional parameter to skip database check if article existence is already known
 */
export const validateArticleExists = async (articleId: string, articleExists: boolean | null = null) => {
  // If the articleExists parameter is provided and is true, we can skip the check
  if (articleExists === true) return true;
  
  // For mock articles, we know they exist in our mock data
  if (isMockArticleId(articleId)) return true;
  
  // For real articles, you would typically check if they exist in the database
  // This function can be expanded to make an actual database check
  // For now we're just allowing the operation
  return true;
};

/**
 * Creates proper article records in the database for mock articles if needed
 */
export const ensureArticleInDatabase = async (articleId: string) => {
  // This utility can be used later if we want to automatically create article 
  // records in the database for mock articles before referencing them
  return true;
};
